// v10/src/components/admin/inventory/modals/CreateInventoryItemModal.js
//
// Modal "Nuevo producto" del Stock de repuestos (A8) — Tarea 2.
// Toggle entre dos modos:
//   • template → seleccionar de templates del catálogo (POST source=template)
//   • custom   → ingresar todos los campos manualmente (POST source=custom)
//
// Endpoints:
//   GET  /api/inventory-templates?category=...   (templates filtrados)
//   POST /api/inventory-items                    (crear producto)
//   PUT  /api/inventory-items/:id                (workaround backend, ver nota)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Loader, Plus, Package, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../../services/inventoryService';
import { CATEGORY_KEYS } from '../categoryIcons';

// Mismos enums que el backend (inventoryItemsController.VALID_STOCK_UNITS)
const STOCK_UNITS = [
  'unit', 'liter', 'milliliter', 'box', 'pack', 'meter',
  'kg', 'gram', 'gallon', 'quart', 'foot',
];

const TRACKING_MODES = ['quantity', 'serialized'];

const CreateInventoryItemModal = ({
  isOpen,
  onClose,
  onSuccess,
  darkMode,
}) => {
  const { t } = useTranslation('admin');

  const [mode, setMode] = useState('template');

  // Modo catálogo
  const [tplCategory, setTplCategory] = useState('');
  const [tplOptions, setTplOptions]   = useState([]);
  const [tplLoading, setTplLoading]   = useState(false);
  const [tplId, setTplId]             = useState('');
  const [tplMinStock, setTplMinStock] = useState('');
  const [tplNotes, setTplNotes]       = useState('');

  // Modo custom
  const [form, setForm] = useState({
    name: '',
    category: '',
    manufacturer: '',
    product_code: '',
    description: '',
    tracking_mode: 'quantity',
    stock_unit: 'unit',
    purchase_unit: '',
    units_per_purchase: '',
    minimum_stock_quantity: '',
    notes: '',
  });
  const [universal, setUniversal] = useState(true);
  const [compats, setCompats] = useState([]); // [{ aircraft_manufacturer, aircraft_model_pattern }]

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isMounted = useRef(true);
  useEffect(() => () => { isMounted.current = false; }, []);

  // ESC para cerrar
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, submitting]);

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setMode('template');
      setTplCategory(''); setTplOptions([]); setTplId(''); setTplMinStock(''); setTplNotes('');
      setForm({
        name: '', category: '', manufacturer: '', product_code: '', description: '',
        tracking_mode: 'quantity', stock_unit: 'unit', purchase_unit: '',
        units_per_purchase: '', minimum_stock_quantity: '', notes: '',
      });
      setUniversal(true);
      setCompats([]);
      setError('');
    }
  }, [isOpen]);

  // Cargar templates al elegir categoría
  useEffect(() => {
    if (mode !== 'template' || !tplCategory) {
      setTplOptions([]);
      setTplId('');
      return undefined;
    }
    let cancelled = false;
    setTplLoading(true);
    setTplId('');
    inventoryService.listTemplates({ category: tplCategory })
      .then((res) => {
        if (cancelled) return;
        setTplOptions(res?.data?.data || []);
      })
      .catch(() => {
        if (cancelled) return;
        setTplOptions([]);
      })
      .finally(() => {
        if (!cancelled) setTplLoading(false);
      });
    return () => { cancelled = true; };
  }, [mode, tplCategory]);

  const selectedTemplate = useMemo(
    () => tplOptions.find((o) => o.id === tplId) || null,
    [tplOptions, tplId],
  );

  const setCustomField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addCompat = () => setCompats((arr) => [...arr, { aircraft_manufacturer: '', aircraft_model_pattern: '' }]);
  const updateCompat = (idx, field, value) =>
    setCompats((arr) => arr.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  const removeCompat = (idx) => setCompats((arr) => arr.filter((_, i) => i !== idx));

  // ── Validación ───────────────────────────────────────────────────
  const validation = useMemo(() => {
    const errors = [];
    if (mode === 'template') {
      if (!tplId) errors.push(t('inventory_modal_create_validation_template_required'));
      if (tplMinStock !== '') {
        const n = Number(tplMinStock);
        if (!Number.isFinite(n) || n < 0) errors.push(t('inventory_modal_create_validation_minimum_negative'));
      }
    } else {
      if (!form.name.trim()) errors.push(t('inventory_modal_create_validation_name_required'));
      if (!form.category)    errors.push(t('inventory_modal_create_validation_category_required'));
      if (!form.tracking_mode) errors.push(t('inventory_modal_create_validation_tracking_required'));
      if (!form.stock_unit)  errors.push(t('inventory_modal_create_validation_stock_unit_required'));

      if (form.minimum_stock_quantity !== '') {
        const n = Number(form.minimum_stock_quantity);
        if (!Number.isFinite(n) || n < 0) errors.push(t('inventory_modal_create_validation_minimum_negative'));
      }
      if (form.tracking_mode === 'quantity' && form.units_per_purchase !== '') {
        const n = Number(form.units_per_purchase);
        if (!Number.isFinite(n) || n <= 0) errors.push(t('inventory_modal_create_validation_units_per_purchase_positive'));
      }

      if (!universal) {
        const seen = new Set();
        for (const c of compats) {
          const m  = (c.aircraft_manufacturer || '').trim();
          const mp = (c.aircraft_model_pattern || '').trim();
          if (!m || !mp) {
            errors.push(t('inventory_modal_create_validation_compat_incomplete'));
            break;
          }
          const key = `${m.toLowerCase()}::${mp.toLowerCase()}`;
          if (seen.has(key)) {
            errors.push(t('inventory_modal_create_validation_compat_duplicate'));
            break;
          }
          seen.add(key);
        }
      }
    }
    return { errors, ok: errors.length === 0 };
  }, [mode, tplId, tplMinStock, form, universal, compats, t]);

  // ── Submit ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting || !validation.ok) return;

    setSubmitting(true);
    setError('');

    try {
      let createdItem = null;

      if (mode === 'template') {
        // POST con source=template + custom_fields_override (backend bug: a veces
        // no respeta el minimum_stock_quantity del body en modo template, así que
        // hacemos PUT defensivo si el usuario cargó stock mínimo o notas).
        const payload = {
          source: 'template',
          template_id: tplId,
        };
        const overrides = {};
        if (tplMinStock !== '') overrides.minimum_stock_quantity = Number(tplMinStock);
        if (tplNotes.trim()) overrides.notes = tplNotes.trim();
        if (Object.keys(overrides).length) payload.custom_fields_override = overrides;

        const res = await inventoryService.create(payload);
        createdItem = res?.data?.data || null;

        // Workaround backend (item de backlog): forzamos PUT con los campos
        // editables si no coinciden con lo enviado.
        if (createdItem && Object.keys(overrides).length) {
          const needsPut =
            (overrides.minimum_stock_quantity !== undefined &&
              Number(createdItem.minimum_stock_quantity) !== overrides.minimum_stock_quantity) ||
            (overrides.notes !== undefined && (createdItem.notes || '') !== overrides.notes);
          if (needsPut) {
            const putPayload = {};
            if (overrides.minimum_stock_quantity !== undefined) putPayload.minimum_stock_quantity = overrides.minimum_stock_quantity;
            if (overrides.notes !== undefined) putPayload.notes = overrides.notes;
            try {
              const putRes = await inventoryService.update(createdItem.id, putPayload);
              createdItem = putRes?.data?.data || createdItem;
            } catch (_) { /* dejamos el item creado igual */ }
          }
        }
      } else {
        const payload = {
          source: 'custom',
          name: form.name.trim(),
          category: form.category,
          manufacturer: form.manufacturer.trim() || null,
          product_code: form.product_code.trim() || null,
          description: form.description.trim() || null,
          tracking_mode: form.tracking_mode,
          stock_unit: form.stock_unit,
          purchase_unit: form.tracking_mode === 'serialized' ? null : (form.purchase_unit || null),
          units_per_purchase: form.tracking_mode === 'serialized' || form.units_per_purchase === ''
            ? null : Number(form.units_per_purchase),
          minimum_stock_quantity: form.minimum_stock_quantity === '' ? 0 : Number(form.minimum_stock_quantity),
          notes: form.notes.trim() || null,
        };
        if (!universal) {
          payload.compatibilities = compats.map((c) => ({
            aircraft_manufacturer: c.aircraft_manufacturer.trim(),
            aircraft_model_pattern: c.aircraft_model_pattern.trim(),
          }));
        }
        const res = await inventoryService.create(payload);
        createdItem = res?.data?.data || null;
      }

      if (!isMounted.current) return;
      onSuccess?.(createdItem);
      onClose?.();
    } catch (err) {
      if (!isMounted.current) return;
      const msg = err?.response?.data?.message || t('inventory_modal_network_error');
      setError(msg);
    } finally {
      if (isMounted.current) setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // ── Estilos compartidos ──────────────────────────────────────────
  const fgPrimary = darkMode ? 'white' : '#0f172a';
  const fgMuted   = darkMode ? 'rgba(255,255,255,0.45)' : '#64748b';
  const fgFaint   = darkMode ? 'rgba(255,255,255,0.30)' : '#94a3b8';
  const labelStyle = {
    margin: '0 0 6px', fontSize: 12, fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.06em', color: fgFaint,
  };
  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 12,
    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid #e2e8f0',
    background: darkMode ? 'rgba(255,255,255,0.05)' : '#f8fafc',
    color: fgPrimary, fontSize: 14, fontFamily: 'inherit',
    outline: 'none', boxSizing: 'border-box',
  };
  const lockedStyle = { ...inputStyle, opacity: 0.55, cursor: 'not-allowed', background: darkMode ? 'rgba(255,255,255,0.02)' : '#f1f5f9' };

  const tplCategoryOptions = CATEGORY_KEYS.map((k) => ({
    key: k,
    label: t(`inventory_categories_${k}`, { defaultValue: k }),
  }));
  const stockUnitOptions = STOCK_UNITS.map((u) => ({
    key: u,
    label: t(`inventory_purchase_units_${u}`, { defaultValue: u }),
  }));

  const showSerializedNotice = mode === 'custom' && form.tracking_mode === 'serialized';

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
      }}
      onClick={submitting ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 20 }}
        style={{
          width: '100%', maxWidth: 640, borderRadius: 24,
          background: darkMode ? 'rgba(15,23,42,0.98)' : 'white',
          border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
          padding: 28, boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'linear-gradient(135deg,#4f46e5,#6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 16px rgba(79,70,229,0.3)',
            }}>
              <Package style={{ width: 20, height: 20, color: 'white' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: fgPrimary }}>
                {t('inventory_modal_create_title')}
              </h3>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: fgMuted, maxWidth: 380 }}>
                {t('inventory_modal_create_subtitle')}
              </p>
            </div>
          </div>
          <button
            type="button" onClick={onClose} disabled={submitting}
            aria-label={t('inventory_modal_close')}
            style={{
              padding: 8, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: submitting ? 'not-allowed' : 'pointer',
              color: fgMuted,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Toggle modo */}
        <div role="tablist" style={{
          display: 'inline-flex', padding: 4, borderRadius: 12, gap: 4,
          background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
          border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
          marginBottom: 18,
        }}>
          {[
            { id: 'template', label: t('inventory_modal_create_mode_template') },
            { id: 'custom',   label: t('inventory_modal_create_mode_custom') },
          ].map((opt) => {
            const isSel = opt.id === mode;
            return (
              <button
                key={opt.id} type="button" role="tab"
                aria-selected={isSel}
                onClick={() => !submitting && setMode(opt.id)}
                disabled={submitting}
                style={{
                  padding: '8px 14px', borderRadius: 9, border: 'none',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                  background: isSel ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white') : 'transparent',
                  color: isSel ? (darkMode ? 'white' : '#0f172a') : fgMuted,
                  boxShadow: isSel ? '0 2px 8px rgba(15,23,42,0.08)' : 'none',
                  transition: 'all 0.18s',
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit}>
          {mode === 'template' ? (
            <>
              {/* Categoría */}
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_create_custom_category_label')}</p>
                <select
                  value={tplCategory}
                  onChange={(e) => setTplCategory(e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                >
                  <option value="">{t('inventory_modal_create_template_select_category')}</option>
                  {tplCategoryOptions.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Producto */}
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_create_template_select_template')}</p>
                <select
                  value={tplId}
                  onChange={(e) => setTplId(e.target.value)}
                  disabled={!tplCategory || tplLoading}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                >
                  <option value="">
                    {tplLoading
                      ? t('inventory_modal_create_template_loading')
                      : t('inventory_modal_create_template_select_template')}
                  </option>
                  {tplOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}{o.manufacturer ? ` (${o.manufacturer})` : ''}
                    </option>
                  ))}
                </select>
                {tplCategory && !tplLoading && tplOptions.length === 0 && (
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                    {t('inventory_modal_create_template_no_results')}
                  </p>
                )}
              </div>

              {/* Preview del template */}
              {selectedTemplate && (
                <div style={{
                  padding: 14, borderRadius: 14, marginBottom: 14,
                  background: darkMode ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.06)',
                  border: darkMode ? '1px solid rgba(99,102,241,0.20)' : '1px solid rgba(99,102,241,0.18)',
                }}>
                  <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {t('inventory_modal_create_template_preview_title')}
                  </p>
                  <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12.5, color: fgPrimary }}>
                    <div><strong>{t('inventory_modal_create_template_field_tracking')}:</strong> {t(`inventory_modal_create_custom_tracking_${selectedTemplate.default_tracking_mode}`, { defaultValue: selectedTemplate.default_tracking_mode })}</div>
                    <div><strong>{t('inventory_modal_create_template_field_stock_unit')}:</strong> {t(`inventory_purchase_units_${selectedTemplate.default_stock_unit}`, { defaultValue: selectedTemplate.default_stock_unit })}</div>
                    {selectedTemplate.default_purchase_unit && (
                      <div><strong>{t('inventory_modal_create_template_field_purchase_unit')}:</strong> {t(`inventory_purchase_units_${selectedTemplate.default_purchase_unit}`, { defaultValue: selectedTemplate.default_purchase_unit })}</div>
                    )}
                    {selectedTemplate.default_units_per_purchase && (
                      <div><strong>{t('inventory_modal_create_template_field_units_per_purchase')}:</strong> {selectedTemplate.default_units_per_purchase}</div>
                    )}
                  </div>
                  {Array.isArray(selectedTemplate.compatibilities) && (
                    <p style={{ margin: '8px 0 0', fontSize: 12, color: fgMuted }}>
                      {selectedTemplate.compatibilities.length === 0
                        ? t('inventory_modal_create_template_compatibilities_universal')
                        : t('inventory_modal_create_template_compatibilities_count', { count: selectedTemplate.compatibilities.length })}
                    </p>
                  )}
                </div>
              )}

              {/* Stock mínimo + notas */}
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_create_minimum_stock_label')}</p>
                <input
                  type="number" min="0" step="0.01" style={inputStyle}
                  value={tplMinStock}
                  onChange={(e) => setTplMinStock(e.target.value)}
                  placeholder="0"
                />
                <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                  {t('inventory_modal_create_minimum_stock_hint')}
                </p>
              </div>
              <div style={{ marginBottom: 16 }}>
                <p style={labelStyle}>{t('inventory_modal_create_notes_label')}</p>
                <textarea
                  rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
                  value={tplNotes}
                  onChange={(e) => setTplNotes(e.target.value)}
                  placeholder={t('inventory_modal_create_notes_placeholder')}
                />
              </div>
            </>
          ) : (
            <>
              {/* Nombre */}
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_create_custom_name_label')}</p>
                <input
                  type="text" maxLength={200} style={inputStyle}
                  value={form.name}
                  onChange={(e) => setCustomField('name', e.target.value)}
                  placeholder={t('inventory_modal_create_custom_name_placeholder')}
                />
              </div>

              {/* Categoría */}
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_create_custom_category_label')}</p>
                <select
                  value={form.category}
                  onChange={(e) => setCustomField('category', e.target.value)}
                  style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                >
                  <option value="">{t('inventory_modal_create_custom_category_placeholder')}</option>
                  {tplCategoryOptions.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Marca + código */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_create_custom_manufacturer_label')}</p>
                  <input
                    type="text" maxLength={120} style={inputStyle}
                    value={form.manufacturer}
                    onChange={(e) => setCustomField('manufacturer', e.target.value)}
                    placeholder={t('inventory_modal_create_custom_manufacturer_placeholder')}
                  />
                </div>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_create_custom_product_code_label')}</p>
                  <input
                    type="text" maxLength={80} style={inputStyle}
                    value={form.product_code}
                    onChange={(e) => setCustomField('product_code', e.target.value)}
                    placeholder={t('inventory_modal_create_custom_product_code_placeholder')}
                  />
                </div>
              </div>

              {/* Descripción */}
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_create_custom_description_label')}</p>
                <textarea
                  rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
                  value={form.description}
                  onChange={(e) => setCustomField('description', e.target.value)}
                  placeholder={t('inventory_modal_create_custom_description_placeholder')}
                />
              </div>

              {/* Tracking mode */}
              <div style={{ marginBottom: 14 }}>
                <p style={labelStyle}>{t('inventory_modal_create_custom_tracking_label')}</p>
                <div style={{
                  display: 'inline-flex', padding: 4, borderRadius: 12, gap: 4,
                  background: darkMode ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
                  border: darkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid #e2e8f0',
                }}>
                  {TRACKING_MODES.map((tm) => {
                    const isSel = tm === form.tracking_mode;
                    return (
                      <button
                        key={tm} type="button"
                        onClick={() => setCustomField('tracking_mode', tm)}
                        style={{
                          padding: '8px 14px', borderRadius: 9, border: 'none', cursor: 'pointer',
                          background: isSel ? (darkMode ? 'rgba(255,255,255,0.10)' : 'white') : 'transparent',
                          color: isSel ? (darkMode ? 'white' : '#0f172a') : fgMuted,
                          fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                          boxShadow: isSel ? '0 2px 8px rgba(15,23,42,0.08)' : 'none',
                        }}
                      >
                        {t(`inventory_modal_create_custom_tracking_${tm}`)}
                      </button>
                    );
                  })}
                </div>
              </div>

              {showSerializedNotice && (
                <div style={{
                  padding: '10px 14px', borderRadius: 12, marginBottom: 14,
                  background: 'rgba(13,148,136,0.10)', border: '1px solid rgba(13,148,136,0.25)',
                  color: '#0d9488',
                }}>
                  <p style={{ margin: 0, fontSize: 12.5, fontWeight: 600, lineHeight: 1.4 }}>
                    {t('inventory_modal_create_serialized_disclaimer')}
                  </p>
                </div>
              )}

              {/* Stock unit + purchase unit + units per purchase */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_create_custom_stock_unit_label')}</p>
                  <select
                    value={form.stock_unit}
                    onChange={(e) => setCustomField('stock_unit', e.target.value)}
                    style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
                  >
                    {stockUnitOptions.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_create_custom_purchase_unit_label')}</p>
                  <select
                    value={form.purchase_unit}
                    onChange={(e) => setCustomField('purchase_unit', e.target.value)}
                    disabled={form.tracking_mode === 'serialized'}
                    style={{
                      ...(form.tracking_mode === 'serialized' ? lockedStyle : inputStyle),
                      cursor: form.tracking_mode === 'serialized' ? 'not-allowed' : 'pointer',
                      appearance: 'auto',
                    }}
                  >
                    <option value="">{t('inventory_modal_create_custom_purchase_unit_placeholder')}</option>
                    {stockUnitOptions.map((o) => (
                      <option key={o.key} value={o.key}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_create_custom_units_per_purchase_label')}</p>
                  <input
                    type="number" min="0" step="0.01"
                    style={form.tracking_mode === 'serialized' ? lockedStyle : inputStyle}
                    value={form.units_per_purchase}
                    onChange={(e) => setCustomField('units_per_purchase', e.target.value)}
                    disabled={form.tracking_mode === 'serialized'}
                    placeholder="0"
                  />
                  {form.tracking_mode === 'quantity' && (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                      {t('inventory_modal_create_custom_units_per_purchase_hint')}
                    </p>
                  )}
                </div>
                <div>
                  <p style={labelStyle}>{t('inventory_modal_create_minimum_stock_label')}</p>
                  <input
                    type="number" min="0" step="0.01" style={inputStyle}
                    value={form.minimum_stock_quantity}
                    onChange={(e) => setCustomField('minimum_stock_quantity', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Notas */}
              <div style={{ marginBottom: 16 }}>
                <p style={labelStyle}>{t('inventory_modal_create_notes_label')}</p>
                <textarea
                  rows={2} style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }}
                  value={form.notes}
                  onChange={(e) => setCustomField('notes', e.target.value)}
                  placeholder={t('inventory_modal_create_notes_placeholder')}
                />
              </div>

              {/* Compatibilidades */}
              <div style={{ marginBottom: 16 }}>
                <p style={labelStyle}>{t('inventory_modal_create_compatibilities_label')}</p>
                <label style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  padding: '8px 12px', borderRadius: 12,
                  background: universal
                    ? (darkMode ? 'rgba(99,102,241,0.18)' : 'rgba(99,102,241,0.10)')
                    : (darkMode ? 'rgba(255,255,255,0.04)' : '#f1f5f9'),
                  border: universal
                    ? '1px solid rgba(99,102,241,0.30)'
                    : (darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0'),
                  color: universal ? '#6366f1' : fgPrimary,
                  fontSize: 13, fontWeight: 700,
                }}>
                  <input
                    type="checkbox" checked={universal}
                    onChange={(e) => setUniversal(e.target.checked)}
                  />
                  {t('inventory_modal_create_compatibilities_universal_toggle')}
                </label>
                {universal && (
                  <p style={{ margin: '6px 0 0', fontSize: 11, color: fgFaint }}>
                    {t('inventory_modal_create_compatibilities_universal_hint')}
                  </p>
                )}

                {!universal && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {compats.map((c, idx) => (
                      <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
                        <input
                          type="text" maxLength={120} style={inputStyle}
                          value={c.aircraft_manufacturer}
                          onChange={(e) => updateCompat(idx, 'aircraft_manufacturer', e.target.value)}
                          placeholder={t('inventory_modal_create_compatibilities_manufacturer_placeholder')}
                        />
                        <input
                          type="text" maxLength={120} style={inputStyle}
                          value={c.aircraft_model_pattern}
                          onChange={(e) => updateCompat(idx, 'aircraft_model_pattern', e.target.value)}
                          placeholder={t('inventory_modal_create_compatibilities_model_placeholder')}
                        />
                        <button
                          type="button" onClick={() => removeCompat(idx)}
                          aria-label={t('inventory_modal_create_compatibilities_remove')}
                          style={{
                            padding: 10, borderRadius: 12, border: 'none', cursor: 'pointer',
                            background: 'transparent', color: '#ef4444', display: 'flex',
                          }}
                        >
                          <Trash2 style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button" onClick={addCompat}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 12px', borderRadius: 12, border: '1px dashed rgba(99,102,241,0.4)',
                        background: 'transparent', color: '#6366f1', fontWeight: 700, fontSize: 12.5,
                        cursor: 'pointer', alignSelf: 'flex-start', fontFamily: 'inherit',
                      }}
                    >
                      <Plus style={{ width: 14, height: 14 }} />
                      {t('inventory_modal_create_compatibilities_add')}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Validation errors */}
          {validation.errors.length > 0 && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}>
              {validation.errors.map((e, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: 12, color: '#ef4444', fontWeight: 600 }}>
                  • {e}
                </p>
              ))}
            </div>
          )}

          {/* Backend error */}
          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 12,
              background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
              marginBottom: 12,
            }}>
              <p style={{ margin: 0, fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</p>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <motion.button
              type="submit" disabled={submitting || !validation.ok}
              whileHover={!submitting && validation.ok ? { scale: 1.02 } : {}}
              whileTap={!submitting && validation.ok ? { scale: 0.97 } : {}}
              style={{
                flex: 1, padding: 13, borderRadius: 14, border: 'none',
                cursor: submitting || !validation.ok ? 'not-allowed' : 'pointer',
                background: submitting || !validation.ok
                  ? (darkMode ? 'rgba(255,255,255,0.08)' : '#cbd5e1')
                  : 'linear-gradient(135deg,#4f46e5,#6366f1)',
                color: 'white', fontWeight: 700, fontSize: 14, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
              {submitting
                ? <Loader style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                : <Plus style={{ width: 16, height: 16 }} />
              }
              {submitting
                ? t('inventory_modal_create_action_loading')
                : t('inventory_modal_create_action')}
            </motion.button>
            <button
              type="button" onClick={onClose} disabled={submitting}
              style={{
                padding: '13px 18px', borderRadius: 14,
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                background: 'transparent',
                cursor: submitting ? 'not-allowed' : 'pointer',
                fontWeight: 600, fontSize: 14, color: fgMuted, fontFamily: 'inherit',
              }}
            >
              {t('inventory_modal_btn_cancel')}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default CreateInventoryItemModal;
